import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Trash2, Search, ChevronDown, ChevronUp, Filter } from 'lucide-react';

const FilterSection = ({ onFilterChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [filters, setFilters] = useState({
    constructionType: '',
    area: '',
    bedrooms: '',
    terrainWidth: '',
    terrainLength: '',
    terrain: '',
    searchText: ''
  });

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    if (onFilterChange) {
      onFilterChange(newFilters);
    }
  };

  const clearFilters = () => {
    const emptyFilters = {
      constructionType: '',
      area: '',
      bedrooms: '',
      terrainWidth: '',
      terrainLength: '',
      terrain: '',
      searchText: ''
    };
    setFilters(emptyFilters);
    if (onFilterChange) {
      onFilterChange(emptyFilters);
    }
  };

  const constructionTypes = ['Chalé', 'Casa de Campo', 'Casa de Praia', 'Casa Modular', 'Térrea', 'Sobrado'];
  const areaOptions = [50, 100, 150, 200, 250, 300, 350, 399, 450, 499]; // Updated range: 50, 100-499
  const bedroomOptions = [1, 2, 3, 4];

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.constructionType) count++;
    if (filters.area) count++;
    if (filters.bedrooms) count++;
    if (filters.terrain) count++;
    if (filters.terrainWidth || filters.terrainLength) count++;
    if (filters.searchText) count++;
    return count;
  };

  return (
    <section className="bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Filter Toggle Button */}
        <div className="py-4 flex items-center justify-between">
          <Button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center space-x-3 bg-[#00A86B] hover:bg-[#008c5a] text-white px-6 py-3 rounded-lg transition-all shadow-md hover:shadow-lg"
          >
            <Filter size={20} />
            <span className="font-semibold">Filtros de Pesquisa</span>
            {getActiveFiltersCount() > 0 && (
              <span className="bg-white text-[#00A86B] px-2 py-1 rounded-full text-xs font-bold">
                {getActiveFiltersCount()}
              </span>
            )}
            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </Button>
          
          {getActiveFiltersCount() > 0 && (
            <Button
              variant="ghost"
              onClick={clearFilters}
              className="text-red-500 hover:text-red-600 hover:bg-red-50"
            >
              <Trash2 size={18} className="mr-2" />
              Limpar todos os filtros
            </Button>
          )}
        </div>

        {/* Expandable Filter Content */}
        <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isExpanded ? 'max-h-[2000px] opacity-100 pb-8' : 'max-h-0 opacity-0'
        }`}>
        
        {/* Tipo de Construção */}
        <div className="mb-8">
          <h3 className="text-base font-bold text-gray-800 mb-4">
            Tipo de Construção
          </h3>
          <div className="flex flex-wrap gap-3">
            {constructionTypes.map((type) => (
              <Button
                key={type}
                variant={filters.constructionType === type ? 'default' : 'outline'}
                className={`rounded-full px-6 py-2 transition-all ${
                  filters.constructionType === type 
                    ? 'bg-[#00A86B] hover:bg-[#008c5a] text-white shadow-md' 
                    : 'hover:border-[#00A86B] hover:text-[#00A86B]'
                }`}
                onClick={() => handleFilterChange('constructionType', filters.constructionType === type ? '' : type)}
              >
                {type}
              </Button>
            ))}
          </div>
        </div>

        {/* Área Construída */}
        <div className="mb-8">
          <h3 className="text-base font-bold text-gray-800 mb-4">
            Área Construída (m²)
          </h3>
          <div className="flex flex-wrap gap-2">
            {areaOptions.map((area) => (
              <Button
                key={area}
                variant={filters.area === area.toString() ? 'default' : 'outline'}
                className={`rounded-full px-5 py-2 text-sm transition-all ${
                  filters.area === area.toString()
                    ? 'bg-[#00A86B] hover:bg-[#008c5a] text-white shadow-md'
                    : 'hover:border-[#00A86B] hover:text-[#00A86B]'
                }`}
                onClick={() => handleFilterChange('area', filters.area === area.toString() ? '' : area.toString())}
              >
                {area}m²
              </Button>
            ))}
          </div>
        </div>

        {/* Quartos */}
        <div className="mb-8">
          <h3 className="text-base font-bold text-gray-800 mb-4">
            Quartos
          </h3>
          <div className="flex flex-wrap gap-3">
            {bedroomOptions.map((option) => (
              <Button
                key={option}
                variant={filters.bedrooms === option.toString() ? 'default' : 'outline'}
                className={`rounded-full px-6 py-2 transition-all ${
                  filters.bedrooms === option.toString()
                    ? 'bg-[#00A86B] hover:bg-[#008c5a] text-white shadow-md'
                    : 'hover:border-[#00A86B] hover:text-[#00A86B]'
                }`}
                onClick={() => handleFilterChange('bedrooms', filters.bedrooms === option.toString() ? '' : option.toString())}
              >
                {option}
              </Button>
            ))}
          </div>
        </div>

        {/* Filtrar pelo tamanho do seu terreno */}
        <div className="mb-8">
          <h3 className="text-base font-bold text-gray-800 mb-4">
            Filtrar pelo tamanho do seu terreno
          </h3>
          <div className="flex flex-wrap gap-4 items-center">
            <Input
              type="number"
              placeholder="Largura (m)"
              value={filters.terrainWidth}
              onChange={(e) => handleFilterChange('terrainWidth', e.target.value)}
              className="w-40 rounded-lg border-gray-300 focus:border-[#00A86B] focus:ring-[#00A86B]"
            />
            <span className="text-gray-500 font-semibold">×</span>
            <Input
              type="number"
              placeholder="Comprimento (m)"
              value={filters.terrainLength}
              onChange={(e) => handleFilterChange('terrainLength', e.target.value)}
              className="w-40 rounded-lg border-gray-300 focus:border-[#00A86B] focus:ring-[#00A86B]"
            />
          </div>
        </div>

        {/* Principais Terrenos */}
        <div className="mb-8">
          <h3 className="text-base font-bold text-gray-800 mb-4">
            Principais Terrenos
          </h3>
          <div className="flex flex-wrap gap-2">
            {['5x25', '10x20', '10x25', '12x25', '12x30', '12.5x30', '13x30', '15x20', '14x40', '17x30', '20x40', '25x40', '30x40', '50x60'].map((size) => (
              <Button
                key={size}
                variant={filters.terrain === size ? 'default' : 'outline'}
                className={`rounded-full px-5 py-2 text-sm transition-all ${
                  filters.terrain === size 
                    ? 'bg-[#00A86B] hover:bg-[#008c5a] text-white shadow-md' 
                    : 'hover:border-[#00A86B] hover:text-[#00A86B]'
                }`}
                onClick={() => handleFilterChange('terrain', filters.terrain === size ? '' : size)}
              >
                {size}
              </Button>
            ))}
          </div>
        </div>

        {/* Buscar projeto e Ações */}
        <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
          <h3 className="text-base font-bold text-gray-800 mb-4">
            Buscar projeto
          </h3>
          <div className="flex flex-col md:flex-row gap-4">
            <Input
              type="text"
              placeholder="Digite o nome ou código do projeto"
              value={filters.searchText}
              onChange={(e) => handleFilterChange('searchText', e.target.value)}
              className="flex-1 rounded-lg border-gray-300 focus:border-[#00A86B] focus:ring-[#00A86B] h-12"
            />
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="rounded-lg border-red-500 text-red-500 hover:bg-red-500 hover:text-white px-8 h-12 transition-all"
                onClick={clearFilters}
              >
                <Trash2 size={18} className="mr-2" />
                Limpar
              </Button>
              <Button
                className="rounded-lg bg-[#00A86B] hover:bg-[#008c5a] text-white px-8 h-12 shadow-md transition-all hover:shadow-lg"
                onClick={() => {
                  setIsExpanded(false);
                  console.log('Applying filters:', filters);
                }}
              >
                <Search size={18} className="mr-2" />
                Aplicar
              </Button>
            </div>
          </div>
        </div>
        
        </div>
      </div>
    </section>
  );
};

export default FilterSection;
